import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import headerBg from '/public/img/ai/header-bg.png';
import { useRecoilState } from 'recoil';
import {
  aiChatDialogOpenSessionAtom,
  aiChatOpenAtom,
  aiChatMessagesSessionAtom,
  aiChatBootstrapSessionAtom,
} from 'recoil/atoms';
import {
  FC,
  useEffect,
  useState,
  forwardRef,
  useRef,
  useCallback,
} from 'react';
import { AiMessage, PendingMessage, UserMessage } from './atoms/AiChat';
import { ENVIRONMENT } from '../shared/config';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import Rest from 'services/rest';
import { useScreenSize } from 'hooks/useScreenSize';
import { format } from 'date-fns';
import dynamic from 'next/dynamic';

const ReactMarkdown = dynamic(() => import('react-markdown'), {
  ssr: false,
});

const rest = new Rest();

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

const OPEN_DIALOG_DELAY_MS = 10000; // 10 seconds

export enum AI_CHAT_MESSAGE_TYPE {
  AI = 'ai',
  USER = 'user',
}

export type AiChatMessage = {
  type: AI_CHAT_MESSAGE_TYPE;
  message: string;
  timestamp: string;
};

const getDialogSx = (isDelayed: boolean) => ({
  zIndex: 1300,
  width: {
    xs: '100%',
    sm: '100%',
    md: isDelayed ? '100%' : '400px',
  },
  left: {
    xs: 0,
    sm: 0,
    md: isDelayed ? 0 : 'auto',
  },
  '& .MuiDialog-container': {
    position: {
      xs: 'initial',
      sm: 'initial',
      md: isDelayed ? 'initial' : 'absolute',
    },
    bottom: {
      xs: 'initial',
      sm: 'initial',
      md: isDelayed ? 'initial' : 0,
    },
    right: {
      xs: 'initial',
      sm: 'initial',
      md: isDelayed ? 'initial' : 0,
    },
  },
  '& .MuiPaper-root': {
    borderRadius: 2,
    overflow: 'hidden',
  },
});

const getDialogTitleSx = (isDelayed: boolean) => ({
  background: `url(${headerBg.src}) no-repeat center center`,
  backgroundSize: 'cover',
  height: {
    xs: 190,
    sm: 239,
    md: isDelayed ? 239 : 190,
  },
  textAlign: 'right',
  '&.MuiTypography-root': {
    color: '#fff',
    paddingBottom: 10,
    alignContent: 'end',
    fontSize: '0.9rem',
  },
});

const ERROR_MESSAGE =
  'I appologize, but I am unable to assist you at the moment.';

const AiChatDialog: FC = (): JSX.Element => {
  const [isDelayed, setIsDelayed] = useState(false);
  const [pending, setPending] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [messages, setMessages] = useRecoilState(aiChatMessagesSessionAtom);
  const [isOpen, setOpen] = useRecoilState(aiChatOpenAtom);
  const [wasOpenedThisSession, setWasOpenedThisSession] = useRecoilState(
    aiChatDialogOpenSessionAtom,
  );
  const [isBootstrapThisSession, setBootstrapThisSession] = useRecoilState(
    aiChatBootstrapSessionAtom,
  );
  const dialogContentRef = useRef(null);
  const isE2E = process.env.ENVIRONMENT === ENVIRONMENT.E2E;
  const { isSmallScreen, isExtraSmallScreen } = useScreenSize();
  const isCompact = isSmallScreen || isExtraSmallScreen;

  useEffect(() => {
    const timeoutHandler = setTimeout(() => {
      if (!wasOpenedThisSession && !isE2E) {
        setOpen(true);
        setIsDelayed(true);
        setWasOpenedThisSession(true);
      }
    }, OPEN_DIALOG_DELAY_MS);
    return () => clearTimeout(timeoutHandler);
  }, [isE2E, setOpen, setWasOpenedThisSession, wasOpenedThisSession]);

  const handleClose = () => {
    setOpen(false);
    setIsDelayed(false);
  };

  // Scroll to the last message when new messages are added
  useEffect(() => {
    if (messages.length > 1) {
      setTimeout(() => {
        const lastChild =
          dialogContentRef.current?.lastElementChild?.lastElementChild;
        lastChild?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [messages.length]);

  const extractDealPath = (message: string): string | null => {
    const match = message.match(/https?:\/\/[^/]+(\/deals\/\d+)/);
    return match ? match[1] : null;
  };

  const handleReply = useCallback(async () => {
    if (replyMessage.length) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          type: AI_CHAT_MESSAGE_TYPE.USER,
          message: replyMessage,
          timestamp: format(new Date(), 'hh:mm a'),
        },
      ]);

      setReplyMessage('');
    }

    setPending(true);

    let responseMessage;

    try {
      responseMessage = await rest.sendMessageAssistant({
        message: replyMessage,
      });
    } catch (error) {
      console.error('Error sending message to assistant.');
    }

    if (!responseMessage || !responseMessage?.length) {
      setPending(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          type: AI_CHAT_MESSAGE_TYPE.AI,
          message: ERROR_MESSAGE,
          timestamp: format(new Date(), 'hh:mm a'),
        },
      ]);
      return;
    }

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        type: AI_CHAT_MESSAGE_TYPE.AI,
        message: responseMessage.replace(/【\d+:\d+.+】/g, ''),
        timestamp: format(new Date(), 'hh:mm a'),
      },
    ]);

    setPending(false);

    const dealPath = extractDealPath(responseMessage);

    if (dealPath) {
      if (isExtraSmallScreen) {
        setOpen(false);
      }
      // Workaround for the issue with the images cache in gallery
      window.location.href = dealPath;
    }
  }, [isExtraSmallScreen, replyMessage, setMessages, setOpen]);

  // Bootstrap the dialog with a message if it was opened for the first time
  useEffect(() => {
    if (!isBootstrapThisSession && !isE2E) {
      setBootstrapThisSession(true);
      handleReply();
    }
  }, [handleReply, isBootstrapThisSession, isE2E, setBootstrapThisSession]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      hideBackdrop={!isDelayed}
      TransitionComponent={Transition}
      keepMounted
      disableScrollLock={isDelayed || !isCompact}
      disableEnforceFocus
      sx={getDialogSx(isDelayed)}
    >
      <DialogTitle sx={getDialogTitleSx(isDelayed)}>
        Your personal deal scout
      </DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={{
          color: '#fff',
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{ p: 2, maxHeight: 450, minWidth: { md: 380 } }}
        ref={dialogContentRef}
      >
        {messages.map((msg, index) => {
          if (msg.type === AI_CHAT_MESSAGE_TYPE.AI) {
            return (
              <AiMessage key={index} timestamp={format(new Date(), 'hh:mm a')}>
                <ReactMarkdown>{msg.message}</ReactMarkdown>
              </AiMessage>
            );
          }
          if (msg.type === AI_CHAT_MESSAGE_TYPE.USER) {
            return (
              <UserMessage key={index} timestamp={msg.timestamp}>
                {msg.message}
              </UserMessage>
            );
          }

          return null;
        })}
        {pending && <PendingMessage />}
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3 }}>
        <TextField
          size='small'
          variant='standard'
          placeholder='Reply...'
          fullWidth
          value={replyMessage}
          onChange={(e) => setReplyMessage(e.target.value)}
          onKeyDown={(e) => {
            if (!pending && e.key === 'Enter' && replyMessage) {
              handleReply();
            }
          }}
        />
        <IconButton
          color={'primary'}
          sx={{
            backgroundColor: 'primary.main',
            '&:disabled': {
              backgroundColor: 'primary.light',
            },
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
          onClick={handleReply}
          disabled={!replyMessage || pending}
        >
          <ChevronRightIcon sx={{ color: '#fff' }} />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};

export default AiChatDialog;
